import React, { useMemo, useState } from "react";
import Box from "@mui/material/Box";
import { WorklogCalendar } from "./worklogCalendar";
import { Base } from "../../framework/common/base";
import { handleApiError } from "../../services/baseService";
import { fetchSalaryPeriods } from "../../framework/redux/slices/salaryPeriodSlice";
import {
    calculatedWorkHours,
    normalWorkHours,
} from "../../framework/redux/slices/workHoursSlice";
import { useAsyncThunkAction } from "../../hooks/framework/useAsyncThunkAction";
import { useSelectSalaryPeriodByDate } from "../../hooks/worktime/useSelectSalaryPeriod";
import { MainLayout } from "../layout/mainLayout";
import { fetchSalaryRowTypes } from "../../framework/redux/slices/workShiftTimeSlotSlice";
import { fetchPlannedWorkShifts } from "../../framework/redux/slices/plannedWorkShiftSlice";
import { useAppOptionValueEquals } from "../../hooks/framework/useAppOptionValue";
import { OptionWorkShiftPlanningEnabled } from "../../models/common/consts";
import { WorklogSections } from "./worklogSections";

export const WorklogMain = () => {
    const [dateString, setDateString] = useState<string>(Base.dateToDateJSON());

    const startOfWeek = Base.dateToDateJSON(
        Base.dateStartOf(dateString, "week")
    );
    const endOfWeek = Base.dateToDateJSON(Base.dateEndOf(dateString, "week"));

    const selectedSalaryPeriodItem = useSelectSalaryPeriodByDate(dateString);
    const startOfWeekSalaryPeriod = useSelectSalaryPeriodByDate(startOfWeek);
    const endOfWeekSalaryPeriod = useSelectSalaryPeriodByDate(endOfWeek);

    const [plannedShiftsEnabled] = useAppOptionValueEquals(
        OptionWorkShiftPlanningEnabled,
        true
    );

    useAsyncThunkAction(
        () => fetchSalaryPeriods(),
        { onError: handleApiError },
        []
    );

    useAsyncThunkAction(
        () => fetchSalaryRowTypes(),
        { onError: handleApiError },
        []
    );

    useAsyncThunkAction(
        () => {
            if (selectedSalaryPeriodItem?.id) {
                return calculatedWorkHours({
                    salaryPeriodId: selectedSalaryPeriodItem?.id,
                    startDate: selectedSalaryPeriodItem?.startDate,
                    endDate: selectedSalaryPeriodItem?.endDate,
                });
            }
        },
        { onError: handleApiError },
        [selectedSalaryPeriodItem?.id]
    );

    // Fetch salary period for start of week if not same is current period,
    // to show weekly work hours
    useAsyncThunkAction(
        () => {
            if (
                selectedSalaryPeriodItem &&
                startOfWeekSalaryPeriod &&
                selectedSalaryPeriodItem?.id !== startOfWeekSalaryPeriod?.id
            ) {
                return calculatedWorkHours({
                    salaryPeriodId: startOfWeekSalaryPeriod?.id,
                    startDate: startOfWeekSalaryPeriod?.startDate,
                    endDate: startOfWeekSalaryPeriod?.endDate,
                });
            }
        },
        { onError: handleApiError },
        [selectedSalaryPeriodItem?.id, startOfWeekSalaryPeriod?.id]
    );

    // Fetch salary period for end of week if not same is current period,
    // to show weekly work hours
    useAsyncThunkAction(
        () => {
            if (
                selectedSalaryPeriodItem &&
                endOfWeekSalaryPeriod &&
                selectedSalaryPeriodItem?.id !== endOfWeekSalaryPeriod?.id
            ) {
                return calculatedWorkHours({
                    salaryPeriodId: endOfWeekSalaryPeriod?.id,
                    startDate: endOfWeekSalaryPeriod?.startDate,
                    endDate: endOfWeekSalaryPeriod?.endDate,
                });
            }
        },
        { onError: handleApiError },
        [selectedSalaryPeriodItem?.id, endOfWeekSalaryPeriod?.id]
    );

    const fetchStartDate = useMemo(
        () =>
            Base.dateToDateJSON(
                Base.minDate([
                    selectedSalaryPeriodItem?.startDate,
                    Base.dateStartOf(
                        Base.dateStartOf(dateString, "month"),
                        "week"
                    ),
                ])
            ),
        [selectedSalaryPeriodItem?.startDate, dateString]
    );

    const fetchEndDate = useMemo(
        () =>
            Base.dateToDateJSON(
                Base.maxDate([
                    selectedSalaryPeriodItem?.endDate,
                    Base.dateEndOf(Base.dateEndOf(dateString, "month"), "week"),
                ])
            ),
        [selectedSalaryPeriodItem?.startDate, dateString]
    );

    useAsyncThunkAction(
        () =>
            normalWorkHours({
                startDate: fetchStartDate,
                endDate: fetchEndDate,
            }),
        { onError: handleApiError },
        [fetchStartDate, fetchEndDate]
    );

    useAsyncThunkAction(
        () =>
            plannedShiftsEnabled
                ? fetchPlannedWorkShifts({
                    startDate: fetchStartDate,
                    endDate: fetchEndDate,
                })
                : null,
        { onError: handleApiError },
        [fetchStartDate, fetchEndDate, plannedShiftsEnabled]
    );

    return (
        <MainLayout>
            <WorklogCalendar date={dateString} setDate={setDateString} />
            <Box sx={{ p: 2 }}>
                <WorklogSections dateString={dateString} />
            </Box>
        </MainLayout>
    );
};
